<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Course Outstanding</h1>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row mb-3">
            <div class="col-md-4">
              <b-input-group>
                <b-form-input
                  v-model="search"
                  placeholder="Enter matric number or email..."
                  @keyup.enter="searchStudent"
                ></b-form-input>
                <b-input-group-append>
                  <button class="btn btn-info" @click="searchStudent" :disabled="loading || !search.trim()">
                    <b-spinner small v-if="loading"></b-spinner>
                    <b-icon icon="search" v-else></b-icon>
                  </button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>

          <!-- Student Info -->
          <div v-if="student" class="mb-4">
            <div class="card">
              <div class="card-body py-2">
                <div class="row">
                  <div class="col-md-3"><strong>Name:</strong> {{ student.surname }} {{ student.first_name }}</div>
                  <div class="col-md-3"><strong>Matric:</strong> {{ student.matric_no || 'N/A' }}</div>
                  <div class="col-md-3"><strong>Email:</strong> {{ student.email }}</div>
                  <div class="col-md-3"><strong>Programme:</strong> {{ student.degree || 'N/A' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div v-if="summary" class="row mb-4">
            <div class="col-md-3">
              <div class="card text-center border-left-info">
                <div class="card-body py-2">
                  <div class="text-muted small">Total Registered</div>
                  <h4 class="mb-0">{{ summary.total_registered }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card text-center border-left-success">
                <div class="card-body py-2">
                  <div class="text-muted small">Passed</div>
                  <h4 class="mb-0 text-success">{{ summary.total_passed }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card text-center border-left-danger">
                <div class="card-body py-2">
                  <div class="text-muted small">Failed / Absent</div>
                  <h4 class="mb-0 text-danger">{{ summary.total_failed }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card text-center border-left-warning">
                <div class="card-body py-2">
                  <div class="text-muted small">No Result Yet</div>
                  <h4 class="mb-0 text-warning">{{ summary.total_pending }}</h4>
                </div>
              </div>
            </div>
          </div>

          <!-- Outstanding Courses Table -->
          <div v-if="outstanding.length > 0">
            <h5 class="mb-3">Outstanding Courses ({{ outstanding.length }})</h5>
            <b-table
              :items="outstanding"
              :fields="outstandingFields"
              :per-page="perPage"
              :current-page="currentPage"
              responsive
              striped
              hover
              small
            >
              <template #cell(index)="data">
                {{ (currentPage - 1) * perPage + data.index + 1 }}
              </template>
              <template #cell(unit)="data">
                <b-badge variant="info">{{ data.value }}</b-badge>
              </template>
              <template #cell(course_status)="data">
                <b-badge :variant="data.value === 'C' ? 'primary' : 'warning'">
                  {{ data.value === 'C' ? 'Compulsory' : 'Elective' }}
                </b-badge>
              </template>
              <template #cell(remark)="data">
                <b-badge :variant="remarkVariant(data.value)">{{ data.value }}</b-badge>
              </template>
              <template #cell(score)="data">
                {{ data.value !== null ? data.value : '-' }}
              </template>
              <template #cell(grade)="data">
                <strong v-if="data.value">{{ data.value }}</strong>
                <span v-else class="text-muted">-</span>
              </template>
            </b-table>

            <div class="d-flex justify-content-between align-items-center" v-if="outstanding.length > perPage">
              <small class="text-muted">Page {{ currentPage }} of {{ Math.ceil(outstanding.length / perPage) }}</small>
              <b-pagination
                v-model="currentPage"
                :total-rows="outstanding.length"
                :per-page="perPage"
                size="sm"
              ></b-pagination>
            </div>
          </div>

          <div v-else-if="searched && !student" class="text-center text-muted py-4">
            No student found with that matric number or email.
          </div>
          <div v-else-if="searched && student && outstanding.length === 0 && summary" class="text-center py-4">
            <b-icon icon="check-circle" variant="success" font-scale="3"></b-icon>
            <div class="mt-2 text-success"><strong>All courses passed! No outstanding courses.</strong></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CourseOutstanding',
  data() {
    return {
      search: '',
      loading: false,
      searched: false,
      student: null,
      outstanding: [],
      summary: null,
      currentPage: 1,
      perPage: 20,
      outstandingFields: [
        { key: 'index', label: '#' },
        { key: 'code', label: 'Course Code', sortable: true },
        { key: 'description', label: 'Description' },
        { key: 'unit', label: 'Unit' },
        { key: 'course_status', label: 'Type' },
        { key: 'session', label: 'Session' },
        { key: 'semester', label: 'Semester' },
        { key: 'score', label: 'Score' },
        { key: 'grade', label: 'Grade' },
        { key: 'remark', label: 'Remark' },
      ],
    };
  },
  methods: {
    searchStudent() {
      if (!this.search.trim()) return;
      this.loading = true;
      this.searched = true;
      this.student = null;
      this.outstanding = [];
      this.summary = null;
      this.currentPage = 1;

      axios.get('/admin/course-outstanding', { params: { search: this.search.trim() } })
        .then(res => {
          this.student = res.data.student || null;
          this.outstanding = res.data.outstanding || [];
          this.summary = res.data.summary || null;
        })
        .catch(() => {
          Toast.fire({ icon: 'error', title: 'Failed to search' });
        })
        .finally(() => { this.loading = false; });
    },
    remarkVariant(remark) {
      if (remark === 'FAIL') return 'danger';
      if (remark === 'ABS') return 'dark';
      return 'secondary';
    },
  },
};
</script>
